/**
 * Pure preflight decision for the FloorSheet "Parameters" button.
 *
 * Centralizing the ladder here keeps the click handler in
 * `components/projects/FloorSheet.tsx` thin and gives us a unit-testable
 * surface for the validation rules (Pup heat numbers + bevel mandatory
 * before the user can open the parameters / upload flow; Weld ID is
 * collected later per upload mode).
 */

export type FloorSheetParametersPupKey = 'pupOne' | 'pupTwo'

export type FloorSheetParametersPreflightInput = {
  /** True when persisted weld parameter data already exists in the WPS section store. */
  hasPersistedParams: boolean
  weldId: string
  pupOne: string
  pupTwo: string
  /** Bevel index chosen during weld setup; null when the user has not picked one yet. */
  bevelIndex: number | null
}

export type FloorSheetParametersPreflightOutcome =
  /** Persisted data exists; navigate straight to the read-only parameters view. */
  | { kind: 'navigate' }
  /** A Pup heat number is missing; switch to the Bevel Measurements tab and open the matching dropdown. */
  | { kind: 'missing-pup'; pup: FloorSheetParametersPupKey }
  /** Pups set but bevel still missing; show inline error on the Bevel picker. */
  | { kind: 'missing-bevel' }
  /** Preflight passed; open the unified upload dialog with the bevel-specific pass labels. */
  | { kind: 'open-unified-upload-dialog'; bevelIndex: number }

/**
 * Evaluate which branch the Parameters button should take.
 *
 * The order matters: we always check persisted data first (so a fully
 * configured weld stays clickable even if the in-memory form draft is
 * temporarily blank), then the mandatory pickers top-to-bottom, then the
 * bevel index, then finally the happy-path upload dialog.
 */
export function evaluateFloorSheetParametersPreflight(
  input: FloorSheetParametersPreflightInput
): FloorSheetParametersPreflightOutcome {
  if (input.hasPersistedParams) {
    return { kind: 'navigate' }
  }
  if (!input.pupOne.trim()) {
    return { kind: 'missing-pup', pup: 'pupOne' }
  }
  if (!input.pupTwo.trim()) {
    return { kind: 'missing-pup', pup: 'pupTwo' }
  }
  if (input.bevelIndex === null) {
    return { kind: 'missing-bevel' }
  }
  return { kind: 'open-unified-upload-dialog', bevelIndex: input.bevelIndex }
}
